import { useIsWalkIn } from '@store/AuthUser/hooks/useIsWalkIn';
import React from 'react';
import { v4 as uuid } from 'uuid';

import { Tabs } from 'chaika-enums';

import { translate } from '@chaika/i18n/helper';
import { CreateEntryParams, entryService } from '@patient/services/entry/entryService';
import { CreateParams } from '@hooks/useCreateEntry/types';
import { markEntryForAutoStart } from '@components/Dictaphone/utils/autoStartMarker';
import { store } from '@store';
import { queryClient } from '@store/queryClient';
import { ehrAccessKeys } from '@patient/queries/ehrAccessQueries';
import { PopupModifiers } from '@components/Popup';
import { EntryTypePopup } from '@components/Popup/EntryTypePopup/EntryTypePopup';
import { useNavigate } from 'react-router-dom';

export interface CreateEntryOptions {
    recordId: number;
    selectedUser?: string;
    redirectTab?: Tabs;
}

interface Options {
    recordId: number;
    staticEntryTypeId?: string;
    redirectTab?: Tabs;
    withoutPopup?: boolean;
    withCreatedCallback?: boolean;
}

interface EntryCreatingProps {
    patientId: string;
    recordId: number;
    createParams: CreateParams;
    staticEntryTypeId?: string;
    redirectTab?: Tabs;
}

export interface EntryCreatingOptions {
    withoutPopup?: boolean;
    withoutTodayEntriesCheck?: boolean;
    withCreatedCallback?: boolean;
}

interface Props {
    openEntryInNewTab?: boolean;
}

interface Data {
    proceedEntryCreating(props: EntryCreatingProps, options?: EntryCreatingOptions): Promise<void>;
    onCreate(params: CreateParams, createOptionsParams: CreateEntryOptions): Promise<void>;
}

export function useCreateEntry({ openEntryInNewTab }: Props = {}): Data {
    const navigate = useNavigate();
    const { walkIn } = useIsWalkIn();

    async function proceedEntryCreating(
        { patientId, createParams, recordId, staticEntryTypeId, redirectTab }: EntryCreatingProps,
        options?: EntryCreatingOptions,
    ): Promise<void> {
        const { app } = store;
        const todayEntries = await entryService.getTodayEntriesForPatient(patientId);

        if (todayEntries.length > 0 && !options?.withoutTodayEntriesCheck) {
            app.confirmPopupParams = {
                description: translate(todayEntries.length === 1 ? 'alreadyLeftEntry' : 'alreadyLeftEntries'),
                additionalButtonText: todayEntries.length === 1 ? translate('goToEntry') : undefined,
                onAdditionalButtonClick: () => openEntry(recordId, todayEntries[0].id),
                onConfirm: () =>
                    createEntry(createParams, {
                        recordId,
                        staticEntryTypeId,
                        redirectTab,
                        withoutPopup: options?.withoutPopup,
                        withCreatedCallback: options?.withCreatedCallback,
                    }),
            };
        } else {
            await createEntry(createParams, {
                recordId,
                staticEntryTypeId,
                redirectTab,
                withoutPopup: options?.withoutPopup,
                withCreatedCallback: options?.withCreatedCallback,
            });
        }
    }

    async function createEntry(
        { onCreated, ...params }: CreateParams,
        { recordId, staticEntryTypeId, redirectTab, withoutPopup, withCreatedCallback }: Options,
    ): Promise<void> {
        const { app } = store;

        if (withoutPopup) {
            await onCreate(withCreatedCallback ? { ...params, onCreated } : params, { recordId });
        } else {
            app.showPopup({
                withoutCloseButton: true,
                modifiers: [PopupModifiers.Redesign, PopupModifiers.NoPaddings],
                getDescription: () =>
                    React.createElement(EntryTypePopup, {
                        recordId,
                        staticEntryTypeId,
                        redirectTab,
                        onCreate: (params, options) => onCreate({ ...params, onCreated }, options),
                        otherParamsForCreation: params,
                    }),
            });
        }
        app.showConfirmPopup(null);
    }

    async function onCreate(
        { onCreated, medicalCaseId, ...params }: CreateParams,
        { recordId, selectedUser, redirectTab }: CreateEntryOptions,
    ): Promise<void> {
        const { app, authUser } = store;

        app.confirmPopupParams = null;
        app.isOnUpdate = true;

        const id = uuid();
        const doctorId = selectedUser ?? authUser.id;
        const createParams: CreateEntryParams = {
            ...params,
            id,
            doctorId,
        };

        try {
            const resp = await entryService.createEntry(createParams, walkIn, medicalCaseId);
            await queryClient.invalidateQueries({ queryKey: ehrAccessKeys.all });

            markEntryForAutoStart(id);

            const url = `/patient/${recordId}${resp ? resp.redirectUrl : ''}${
                redirectTab ? `?tab=${redirectTab}` : ''
            }`;
            await onCreated?.(id);

            processOpenEntry(url);
        } catch (e: any) {
            app.showPopup({
                title: translate('errorOnCreate'),
                description: `${translate('error')}: ${e.message}`,
            });
        }
        app.isOnUpdate = false;
    }

    function openEntry(recordId: number, entryId: string) {
        processOpenEntry(`/patient/${recordId}/entry/${entryId}`);
    }

    function processOpenEntry(url: string): void {
        const { app } = store;
        app.confirmPopupParams = null;

        if (openEntryInNewTab) {
            const newTab = window.open(url, '_blank');
            if (!newTab) {
                navigate(url);
            }
        } else {
            navigate(url);
        }
    }

    return {
        onCreate,
        proceedEntryCreating,
    };
}
